import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";

// Marketing - kept eager so the prerendered HTML hydrates without code-split waits.
import { HomePage } from "@/routes/marketing/HomePage";
import { PrivacyPage } from "@/routes/marketing/PrivacyPage";
import { TermsPage } from "@/routes/marketing/TermsPage";
import { RefundsPage } from "@/routes/marketing/RefundsPage";

// Auth (lazy)
const SignInPage = lazy(() =>
  import("@/routes/auth/SignInPage").then((m) => ({ default: m.SignInPage })),
);
const CheckEmailPage = lazy(() =>
  import("@/routes/auth/CheckEmailPage").then((m) => ({
    default: m.CheckEmailPage,
  })),
);
const AuthErrorPage = lazy(() =>
  import("@/routes/auth/AuthErrorPage").then((m) => ({
    default: m.AuthErrorPage,
  })),
);

// QA (lazy)
const QaPage = lazy(() =>
  import("@/routes/qa/QaPage").then((m) => ({ default: m.QaPage })),
);
const QaSessionPage = lazy(() =>
  import("@/routes/qa/QaSessionPage").then((m) => ({
    default: m.QaSessionPage,
  })),
);
const QaDonePage = lazy(() =>
  import("@/routes/qa/QaDonePage").then((m) => ({ default: m.QaDonePage })),
);

// Protected app (lazy)
const ProtectedRoute = lazy(() =>
  import("@/components/providers/protected-route").then((m) => ({
    default: m.ProtectedRoute,
  })),
);
const DashboardLayout = lazy(() =>
  import("@/routes/dashboard/DashboardLayout").then((m) => ({
    default: m.DashboardLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/routes/dashboard/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  })),
);
const OrdersPage = lazy(() =>
  import("@/routes/dashboard/OrdersPage").then((m) => ({
    default: m.OrdersPage,
  })),
);
const DraftsPage = lazy(() =>
  import("@/routes/dashboard/DraftsPage").then((m) => ({
    default: m.DraftsPage,
  })),
);
const BillingPage = lazy(() =>
  import("@/routes/dashboard/BillingPage").then((m) => ({
    default: m.BillingPage,
  })),
);
const PackagesPage = lazy(() =>
  import("@/routes/dashboard/PackagesPage").then((m) => ({
    default: m.PackagesPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/routes/dashboard/SettingsPage").then((m) => ({
    default: m.SettingsPage,
  })),
);
const SupportPage = lazy(() =>
  import("@/routes/dashboard/SupportPage").then((m) => ({
    default: m.SupportPage,
  })),
);
const NewBriefPage = lazy(() =>
  import("@/routes/brief/NewBriefPage").then((m) => ({
    default: m.NewBriefPage,
  })),
);
const OrderPage = lazy(() =>
  import("@/routes/orders/OrderPage").then((m) => ({ default: m.OrderPage })),
);
const RevisionRequestPage = lazy(() =>
  import("@/routes/orders/RevisionRequestPage").then((m) => ({
    default: m.RevisionRequestPage,
  })),
);

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.key]);

  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <ScrollToTopOnRouteChange />
      <AuthProvider>
        <TooltipProvider delay={200}>
          <Suspense fallback={null}>
            <Routes>
              {/* ── Marketing ──────────────────────────────────────── */}
              <Route path="/" element={<HomePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/refunds" element={<RefundsPage />} />

              {/* ── Auth ───────────────────────────────────────────── */}
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/signin/check-email" element={<CheckEmailPage />} />
              <Route path="/auth/error" element={<AuthErrorPage />} />

              {/* ── QA (public - no auth required) ─────────────────── */}
              <Route path="/qa" element={<QaPage />} />
              <Route path="/qa/:sessionId" element={<QaSessionPage />} />
              <Route path="/qa/:sessionId/done" element={<QaDonePage />} />

              {/* ── Protected app ──────────────────────────────────── */}
              <Route element={<ProtectedRoute />}>
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/dashboard/orders" element={<OrdersPage />} />
                  <Route path="/dashboard/drafts" element={<DraftsPage />} />
                  <Route path="/dashboard/billing" element={<BillingPage />} />
                  <Route path="/dashboard/packages" element={<PackagesPage />} />
                  <Route path="/dashboard/settings" element={<SettingsPage />} />
                  <Route path="/dashboard/support" element={<SupportPage />} />
                </Route>

                <Route element={<DashboardLayout />}>
                  <Route path="/brief/new" element={<NewBriefPage />} />
                  <Route path="/orders/:id" element={<OrderPage />} />
                  <Route
                    path="/orders/:id/revision-request"
                    element={<RevisionRequestPage />}
                  />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </TooltipProvider>
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}
